import React, { CSSProperties, ReactElement, Ref } from 'react';
import classnames from 'classnames';
import { FlexPanel } from '../flexpanel';
import { PaddingType } from '../panel';
import {extractDomProps} from '../../../utils/DomPropsExtractor';
import { isElementOfType } from 'react-dom/test-utils';

/**
 * {@docCategory VerticalPanel}
 */
export interface VerticalPanelProps {
   /**
    * Determines the position of the children in the container.
    *
    * @defaultvalue top
    */
   align?: 'top' | 'center' | 'bottom';

   /**
    * For children that do not stretch horizontally across the container, this setting
    * determines their horizontal alignment.
    *
    * @defaultvalue left
    */
   horizontalAlign?: 'left' | 'center' | 'right';

   /**
    * Gap between panels.
    *
    * @defaultvalue undefined
    */
   gap?: boolean | 'small' | 'medium' | 'large';

   /**
    * True to stretch the children.
    *
    * @defaultvalue false
    */
   stretchChildren?: boolean;

   /**
    * Determines if the component has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue false
    */
   padding?: PaddingType;

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * Used to enable the built in overflow configuration for the content area.
    * @defaultvalue false
    */
   scroll?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width and height.
    *
    * @defaultvalue true
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width.
    *
    * @defaultvalue true
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers height.
    *
    * @defaultvalue true
    */
   verticalFill?: boolean;

   /**
    * The width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    *
    * @defaultvalue '100%' if horizontalFill is true; otherwise 'max-content'
    */
   width?: string | number;

   /**
    * The height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    *
    * @defaultvalue '100%' if verticalFill is true; otherwise 'max-content'
    */
   height?: string | number;

   /**
    * The minimum width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The minimum height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;

   /**
    * Sets the reference property
    */
   ref?: Ref<any>;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;

   /**
    * Called when the panel is clicked.
    */
   onClick?: () => void;
}

const childrenWithFlex = (props: React.PropsWithChildren<VerticalPanelProps>) => {

   const childrenArray: ReactElement[] = [];

   React.Children.forEach(props.children, (child: any) => {
      if (isElementOfType(child, React.Fragment)) {
         child = (child.props as any).children;
      }
      childrenArray.push(child);
   });

   return React.Children.map(childrenArray, (child, index) => {
      if (props.stretchChildren && React.isValidElement(child)) {
         const style: CSSProperties = Object.assign({},
                                                    (child as React.ReactElement).props ? (child as React.ReactElement).props.style : {});
         style.flex = 1;
         return React.cloneElement(child as React.ReactElement, { style: style });
      }

      return child;
   });
};

export const VerticalPanel: React.FC<React.PropsWithChildren<VerticalPanelProps>> = React.forwardRef<HTMLDivElement>(
   function VerticalPanel(props: React.PropsWithChildren<VerticalPanelProps>, ref: Ref<HTMLDivElement>) {
      const align = props.align || 'top';
      const horizontalAlign = props.horizontalAlign ? (props.horizontalAlign === 'left' ? 'flex-start' : props.horizontalAlign === 'right' ? 'flex-end' : 'center') : undefined;
      const classNames = classnames('uf-vertical-panel', props.className);

      return (
         <FlexPanel
            {...extractDomProps(props)}
            flexDirection={'column'}
            justifyContent={align === 'top' ? 'flex-start' : align === 'bottom' ? 'flex-end' : 'center'}
            alignItems={horizontalAlign}
            flexWrap={'nowrap'}
            width={props.width}
            height={props.height}
            minWidth={props.minWidth}
            minHeight={props.minHeight}
            maxWidth={props.maxWidth}
            maxHeight={props.maxHeight}
            border={props.border}
            padding={props.padding}
            paddingLeft={props.paddingLeft}
            paddingRight={props.paddingRight}
            paddingTop={props.paddingTop}
            paddingBottom={props.paddingBottom}
            scroll={props.scroll}
            fill={props.fill}
            horizontalFill={props.horizontalFill}
            verticalFill={props.verticalFill}
            gap={props.gap}
            className={classNames}
            style={props.style}
            ref={ref}
            id={props.id}
         >
            {childrenWithFlex(props)}
         </FlexPanel>
      );
   });
